import React, { useContext, useEffect, useState } from 'react';
import { CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Descriptions,
  Empty,
  Input,
  List,
  Modal,
  notification,
  Space,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';

import Auth0UserDetailsContext from '@totem/components/services/remoteAccess/auth0users/details/Auth0UserDetailsContext';
import StepResultsModal from '@totem/components/services/remoteAccess/auth0users/details/StepResultsModal';
import {
  IBAuth0RoleSummary,
  MultiStepResults,
  RemoteAccessUserRolesAddRequest,
} from '@totem/components/services/remoteAccess/auth0users/details/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  AUTH0_ADD_USER_ROLES_ENDPOINT,
  AUTH0_ASSIGNABLE_ROLES_ENDPOINT,
} from '@totem/utilities/endpoints';

const { Text } = Typography;

const EMPTY_ID = '000000000000000000000000';

const displayRef = (id?: string, name?: string) => {
  if (!id || id === EMPTY_ID || !name) return '';
  return name;
};

const roleLocation = (role: IBAuth0RoleSummary): string => {
  const parts = [
    displayRef(
      role.hierarchyReference?.building?.id,
      role.hierarchyReference?.building?.name,
    ),
    displayRef(
      role.hierarchyReference?.region?.id,
      role.hierarchyReference?.region?.name,
    ),
    displayRef(
      role.hierarchyReference?.organization?.id,
      role.hierarchyReference?.organization?.name,
    ),
  ].filter(Boolean);

  if (parts.length > 0) {
    return parts.join(' / ');
  }
  return role.isTopLevel ? 'Top-level (organization-wide)' : '';
};

const splitName = (name: string): [string, string] => {
  const trimmed = (name ?? '').trim();
  if (!trimmed) {
    return ['', ''];
  }
  const spaceIdx = trimmed.indexOf(' ');
  if (spaceIdx === -1) {
    return [trimmed, ''];
  }
  return [trimmed.slice(0, spaceIdx), trimmed.slice(spaceIdx + 1)];
};

type Props = {
  visible: boolean;
  onClose: () => void;
};

const AddRolesDialog = ({ visible, onClose }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { user, roles, onRefresh } = useContext(Auth0UserDetailsContext);

  const [step, setStep] = useState<'select' | 'confirm' | 'results'>('select');
  const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
  const [assignableRoles, setAssignableRoles] = useState<IBAuth0RoleSummary[]>(
    [],
  );
  const [searchText, setSearchText] = useState<string>('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [sendInvite, setSendInvite] = useState<boolean>(true);
  const [sending, setSending] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<MultiStepResults | null>(null);

  const isInternal = permissions.internal.hasPermissions(userProfile);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setStep('select');

    setSearchText('');

    setSelectedRoleIds([]);

    setSendInvite(true);

    setLastResult(null);

    setLoadingRoles(true);

    fetch(AUTH0_ASSIGNABLE_ROLES_ENDPOINT, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: IBAuth0RoleSummary[]) => {
        setAssignableRoles(Array.isArray(result) ? result : []);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load assignable roles.' });
      })
      .finally(() => setLoadingRoles(false));
  }, [visible, getApiToken]);

  const existingRoleIds = new Set(roles.map((role) => role.id));
  const pickableRoles = assignableRoles.filter(
    (role) => !existingRoleIds.has(role.id),
  );
  const filteredRoles = pickableRoles.filter((role) =>
    role.name.toLowerCase().includes(searchText.toLowerCase()),
  );
  const selectedRoles = pickableRoles.filter((role) =>
    selectedRoleIds.includes(role.id),
  );

  const removeSelectedRole = (roleId: string) => {
    setSelectedRoleIds(selectedRoleIds.filter((id) => id !== roleId));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    if (!user || sending) {
      return;
    }
    setSending(true);

    const [givenName, familyName] = splitName(user.item.name);
    const payload: RemoteAccessUserRolesAddRequest = {
      email: user.item.email,
      givenName,
      familyName,
      sendInvite,
      roles: selectedRoles.map((role) => ({
        roleId: role.id,
        roleName: role.name,
      })),
    };

    fetch(AUTH0_ADD_USER_ROLES_ENDPOINT, {
      method: 'PATCH',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result: MultiStepResults) => {
        onRefresh();

        if (isInternal) {
          setLastResult(result);

          setStep('results');

          return;
        }

        if (result?.success) {
          notification.success({
            message: 'Roles added',
            description: `Added ${selectedRoles.length} role(s) to ${user.item.email}.`,
          });

          onClose();
        } else {
          notification.error({
            message: 'Error adding roles',
            description:
              result?.error ??
              'An error occurred while adding the selected roles.',
            duration: 0,
          });
        }
      })
      .catch(() => {
        notification.error({
          message: 'Error adding roles',
          description: 'An error occurred while adding the selected roles.',
          duration: 0,
        });
      })
      .finally(() => setSending(false));
  };

  const columns: ColumnsType<IBAuth0RoleSummary> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (aRow, bRow) => aRow.name.localeCompare(bRow.name),
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, record) => record.role?.description ?? '',
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => roleLocation(record),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedRoleIds,
    onChange: (keys: React.Key[]) => setSelectedRoleIds(keys as string[]),
    // Without this, antd's Table drops selected keys that aren't present in
    // the current (filtered) dataSource as soon as another row is toggled —
    // exactly the "search hides my picks" bug we're fixing here.
    preserveSelectedRowKeys: true,
  };

  if (step === 'confirm') {
    return (
      <Modal
        open={visible}
        onCancel={handleClose}
        title={<ModalTitle>Confirm Add Roles</ModalTitle>}
        footer={
          <ModalFooter>
            <Button onClick={() => setStep('select')} disabled={sending}>
              Back
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={sending}>
              Confirm
            </Button>
          </ModalFooter>
        }
      >
        <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="User">
            <Space>
              <Avatar size="small" src={user?.item?.picture} />
              <Text>{user?.item?.name}</Text>
              <Text type="secondary">{user?.item?.email}</Text>
            </Space>
          </Descriptions.Item>
        </Descriptions>

        <Text strong>
          The following {selectedRoles.length} role(s) will be added:
        </Text>
        <List
          size="small"
          bordered
          style={{ marginTop: 8 }}
          dataSource={selectedRoles}
          renderItem={(role) => (
            <List.Item>
              <List.Item.Meta
                title={role.name}
                description={roleLocation(role) || undefined}
              />
            </List.Item>
          )}
        />

        {isInternal && (
          <Checkbox
            checked={sendInvite}
            onChange={(evt) => setSendInvite(evt.target.checked)}
            style={{ marginTop: 16 }}
          >
            Send remote access invite (applies if a top-level role is included)
          </Checkbox>
        )}
      </Modal>
    );
  }

  if (step === 'results') {
    return (
      <StepResultsModal
        visible={visible}
        title="Add Roles Result"
        result={lastResult}
        successMessage="Roles added"
        failureMessage="Add roles failed"
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      width={800}
      title={<ModalTitle>Add Roles to {user?.item?.email}</ModalTitle>}
      footer={
        <ModalFooter>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => setStep('confirm')}
            disabled={selectedRoleIds.length === 0}
          >
            Next ({selectedRoleIds.length} selected)
          </Button>
        </ModalFooter>
      }
    >
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Search roles by name"
        value={searchText}
        onChange={(evt) => setSearchText(evt.target.value)}
        style={{ marginBottom: 12 }}
      />

      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">Selected roles:</Text>
        <div style={{ marginTop: 4, minHeight: 32 }}>
          {selectedRoles.length === 0 ? (
            <Text type="secondary" italic>
              None selected yet.
            </Text>
          ) : (
            selectedRoles.map((role) => (
              <span key={role.id}>
                <Tag
                  closable
                  closeIcon={<CloseCircleOutlined />}
                  onClose={() => removeSelectedRole(role.id)}
                  style={{ marginBottom: 4 }}
                >
                  {role.name}
                </Tag>
                &nbsp;
              </span>
            ))
          )}
        </div>
      </div>

      {!loadingRoles && pickableRoles.length === 0 ? (
        <Empty description="No assignable roles found." />
      ) : (
        <IBTable<IBAuth0RoleSummary>
          columns={columns}
          dataSource={filteredRoles}
          loading={loadingRoles}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      )}

      {existingRoleIds.size > 0 && (
        <Alert
          style={{ marginTop: 12 }}
          type="info"
          showIcon
          message="Roles the user already has are excluded from this list."
        />
      )}
    </Modal>
  );
};

export default AddRolesDialog;
